import { useEffect, useMemo, useState } from "react";
import { Switch } from "@headlessui/react";
import { RuleList } from "./RuleList";
import RuleItemBuilder from "./RuleItemBuilder";
import RuleLogList from "./RuleLogList";
import RuleValidator from "./RuleValidator";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useCurrentSize } from "../../CurrentSize";
import { useQuery } from "../../AppLayout";
import { updateCurrentUserOnboardingSteps } from "../../api/UserApi";
import UserAction from "../../actions/UserAction";
import RuleAction from "../../actions/RuleAction";
import LayoutAction from "../../actions/LayoutAction";
import BucketAction from "../../actions/BucketAction";
import TemplateAction from "../../actions/TemplateAction";
import { RuleItemTypes, TemplateContexts } from "../../constants/action-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { CheckCircleIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  SquareKanban,
  User,
  Building,
  Package,
  Plug,
  Bot,
  MessageSquare,
  RefreshCw,
  Mail,
  PenLine,
  History,
} from "lucide-react";
import ChartLoading from "../../insight/components/ChartLoading";
import type { WorkflowNodeCategory, NodeBadge } from "../workflow-editor/types";
import { events as eventDefinitions, actions as actionDefinitions } from "./ruleItemDefinitions";
import RuleItemAppIcon from "./RuleItemAppIcon";
import { isGtmHourlySequenceRule } from "../utils/gtmManagedRule";

// ─── Category definitions ───
const triggerCategories = [
  {
    key: "records",
    label: "Records",
    icon: SquareKanban,
    filter: (e: any) =>
      ["SALE"].includes(e.data?.type) &&
      !e.data?.type?.startsWith("JIRA") &&
      e.data?.type !== "JSM" &&
      e.data?.type !== "HUBSPOT",
  },
  {
    key: "contacts",
    label: "Contacts",
    icon: User,
    filter: (e: any) => e.data?.type === "CONTACT",
  },
  {
    key: "accounts",
    label: "Accounts",
    icon: Building,
    filter: (e: any) => e.data?.type === "COMPANY",
  },
  {
    key: "products",
    label: "Products",
    icon: Package,
    filter: (e: any) => e.data?.type === "PRODUCT",
  },
  {
    key: "integrations",
    label: "Integrations",
    icon: Plug,
    filter: (e: any) => ["JIRA", "JSM", "HUBSPOT"].includes(e.data?.type),
  },
];

const actionCategories = [
  {
    key: "records",
    label: "Records",
    icon: SquareKanban,
    filter: (a: any) =>
      a.data?.type === "SALE" ||
      (a.data?.type === "SALE" && !a.data?.type) ||
      [
        "move_sale_to_funnel",
        "change_status",
        "assign_person",
        "clear_assignees",
        "update_column_to",
        "update_column_from_to",
      ].includes(a.value),
  },
  { key: "ai", label: "AI", icon: Bot, filter: (a: any) => a.data?.type === "AI_FIELD" },
  {
    key: "messaging",
    label: "Messaging",
    icon: MessageSquare,
    filter: (a: any) => ["SLACK", "EMAIL"].includes(a.data?.type),
  },
  {
    key: "conversions",
    label: "Conversions",
    icon: RefreshCw,
    filter: (a: any) => a.value?.startsWith("convert_"),
  },
  {
    key: "marketing",
    label: "Marketing",
    icon: Mail,
    filter: (a: any) => a.data?.type === "MAILCHIMP",
  },
];

// ─── Helpers ───
function getCategoryColors(cat: WorkflowNodeCategory) {
  switch (cat) {
    case "trigger":
      return { bg: "#dbeafe", fill: "#2563eb", border: "#e5e7eb", headerBg: "#f9fafb" };
    case "ai":
      return { bg: "#f3e8ff", fill: "#6366f1", border: "#a78bfa", headerBg: "#faf5ff" };
    case "condition":
      return { bg: "#fee2e2", fill: "#ef4444", border: "#ef4444", headerBg: "#fef2f2" };
    default:
      return { bg: "#dcfce7", fill: "#16a34a", border: "#e5e7eb", headerBg: "#f9fafb" };
  }
}

function getBadgeColor(badge: NodeBadge): string {
  switch (badge) {
    case "Agent":
      return "bg-indigo-50 text-indigo-600";
    case "AI":
      return "bg-gray-100 text-gray-500";
    case "Conditions":
      return "bg-red-50 text-red-500";
    case "Slack":
      return "bg-emerald-50 text-emerald-600";
    case "Email":
      return "bg-sky-50 text-sky-600";
    case "Mailchimp":
      return "bg-yellow-50 text-yellow-700";
    case "Jira":
      return "bg-blue-50 text-blue-600";
    case "HubSpot":
      return "bg-orange-50 text-orange-600";
    case "Records":
      return "bg-gray-100 text-gray-600";
    case "Action":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

function getCategory(data: any, isEvent: boolean): WorkflowNodeCategory {
  if (isEvent) return "trigger";
  if (data?.type === "AI_FIELD") return "ai";
  return "action";
}

function getBadge(data: any, isEvent: boolean): NodeBadge {
  if (isEvent && data?.type === "SCHEDULED") return "Trigger";
  if (isEvent) return "Records";
  if (data?.type === "AI_FIELD") return data?.aiFieldAction === "researchRecord" ? "Agent" : "AI";
  if (data?.type === "SLACK") return "Slack";
  if (data?.type === "EMAIL") return "Email";
  return "Action";
}

// ─── Main App ───
export default function RuleApps() {
  const { projectId, ruleId }: any = useParams();
  const onboarding = useQuery().get("onboarding") || false;
  const appKey = useQuery().get("key") || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentSize = useCurrentSize();
  const rule = useSelector((state: any) => state.ruleReducer.get("rule"));
  const ruleMetrics = useSelector((state: any) => state.ruleReducer.get("ruleMetrics"));
  const ruleAction = RuleAction as any;

  const [activeTab, setActiveTab] = useState<"editor" | "runs">("editor");
  const [selectedItem, setSelectedItem] = useState<{ type: string; index: number } | null>(null);
  const [selectingBlock, setSelectingBlock] = useState<"trigger" | "action" | null>(null);
  const [blockSearchQuery, setBlockSearchQuery] = useState("");

  // ─── Rule form state ───
  const [ruleForm, setRuleForm] = useState<any>({
    events: [],
    actions: [],
    projectId,
    id: "",
    enabled: true,
  });

  const emptyRuleItem = { data: {}, value: "" };
  const isNewRule = ruleId === "new";
  const isManagedGtmRule = useMemo(
    () => !isNewRule && isGtmHourlySequenceRule(rule),
    [isNewRule, rule]
  );

  useEffect(() => {
    if (!!ruleId && ruleId === rule.get("id")) {
      document.title = [
        rule.getIn(["startEvent", "data", "description"]),
        rule.getIn(["startEvent", "data", "to", "label"]) ? "to" : null,
        rule.getIn(["startEvent", "data", "to", "label"]),
      ]
        .filter(Boolean)
        .join(" ");
    } else {
      document.title = `Automation List`;
    }
  }, [ruleId, rule]);

  useEffect(() => {
    if (ruleId) {
      if (isNewRule) {
        setRuleForm({
          projectId,
          enabled: true,
          events: [emptyRuleItem],
          actions: [emptyRuleItem],
          id: "",
        });
        setSelectedItem({ type: "events", index: 0 });
        setSelectingBlock("trigger");
        setBlockSearchQuery("");
      } else {
        dispatch(RuleAction.getRule(ruleId));
        dispatch(RuleAction.getRuleMetrics(ruleId));
      }
      dispatch(BucketAction.getBuckets(projectId, "SALE"));
      dispatch(TemplateAction.getAttributes([TemplateContexts.SALE, TemplateContexts.RECIPIENT]));
    }
  }, [ruleId, appKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rule.get("id")) {
      setRuleForm({
        events: rule.get("events").toJS(),
        actions: rule.get("actions").toJS(),
        projectId,
        enabled: rule.get("enabled"),
        id: rule.get("id") || "",
      });
    }
  }, [rule]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (isManagedGtmRule) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Automation"),
          type: "error",
          message: t("This GTM sequence automation is managed by the system and cannot be updated"),
        })
      );
      return;
    }
    await dispatch(ruleAction.saveRule(ruleForm));
    if (onboarding) {
      updateCurrentUserOnboardingSteps("create-workflow").then(() => {
        dispatch(UserAction.getCurrentUser());
      });
    }
    dispatch(
      LayoutAction.setNotification({
        title: t("Automation"),
        type: "success",
        message: isNewRule
          ? t("Automation created successfully")
          : t("Automation updated successfully"),
      })
    );
    navigate(`/workspaces/${projectId}/automations`);
  };

  const handleToggleEnabled = async () => {
    await dispatch(RuleAction.enableRule(ruleId));
    const newEnabled = !ruleForm.enabled;
    setRuleForm((prev: any) => ({ ...prev, enabled: newEnabled }));
    dispatch(
      LayoutAction.setNotification({
        title: t("Automation"),
        type: "success",
        message: newEnabled
          ? t("Automation activated successfully")
          : t("Automation deactivated successfully"),
      })
    );
  };

  const handleFieldChange = (fieldId: string, index: number, fieldValue: any) => {
    const isEventChange = fieldId === "events" && index === 0;
    const eventChanged =
      isEventChange &&
      ruleForm.events[0] &&
      (ruleForm.events[0].value !== fieldValue?.value ||
        ruleForm.events[0].data?.inputType !== fieldValue?.data?.inputType);
    setRuleForm({
      ...ruleForm,
      [fieldId]: ruleForm[fieldId].map((item: any, i: number) => (i === index ? fieldValue : item)),
      ...(eventChanged ? { actions: [emptyRuleItem] } : {}),
    });
  };

  const handleRemoveRuleItem = (type: string, ruleIndex: number) => {
    let removedList = fromJS(ruleForm[type]);
    if (ruleIndex === 0) {
      removedList = removedList.set(0, emptyRuleItem);
    } else {
      removedList = removedList.delete(ruleIndex);
    }
    setRuleForm({ ...ruleForm, [type]: removedList.toJS() });
    setSelectedItem(null);
  };

  const handleAddRuleItem = (type: string, ruleIndex: number) => {
    const items = fromJS(ruleForm[type]);
    const newList = items.insert(ruleIndex + 1, emptyRuleItem);
    setRuleForm({ ...ruleForm, [type]: newList.toJS() });
    setSelectedItem({ type, index: ruleIndex + 1 });
    setSelectingBlock(type === "events" ? "trigger" : "action");
    setBlockSearchQuery("");
  };

  const getActionInputType = (index: number) => {
    if (index === 0) return ruleForm.events[0]?.data?.inputType || "";
    for (let i = index - 1; i >= 0; i--) {
      const prevOutputType = ruleForm.actions[i]?.data?.outputType;
      if (prevOutputType) return prevOutputType;
    }
    return ruleForm.events[0]?.data?.inputType || "";
  };

  const isValidRule = () => {
    const invalidActions = ruleForm.actions.filter((item: any) => RuleValidator(item)() === false);
    const invalidEvents = ruleForm.events.filter((item: any) => RuleValidator(item)() === false);
    return invalidActions.length === 0 && invalidEvents.length === 0;
  };

  const isLoadingRule = !isNewRule && rule.get("id") !== ruleId;

  const workflowName =
    ruleForm.events[0]?.label ||
    ruleForm.events[0]?.data?.title ||
    ruleForm.events[0]?.data?.description ||
    t("Untitled Workflow");

  // ─── Build items list ───
  const allItems = useMemo(() => {
    const items: {
      type: string;
      index: number;
      label: string;
      category: WorkflowNodeCategory;
      badge: NodeBadge;
      configured: boolean;
    }[] = [];
    ruleForm.events.forEach((ev: any, idx: number) => {
      const data = ev.data || {};
      const label = ev.label || data.title || data.description || t("Select trigger...");
      items.push({
        type: "events",
        index: idx,
        label,
        category: getCategory(data, true),
        badge: getBadge(data, true),
        configured: !!ev.value,
      });
    });
    ruleForm.actions.forEach((act: any, idx: number) => {
      const data = act.data || {};
      const label = act.label || data.title || data.description || t("Select action...");
      items.push({
        type: "actions",
        index: idx,
        label,
        category: getCategory(data, false),
        badge: getBadge(data, false),
        configured: !!act.value,
      });
    });
    return items;
  }, [ruleForm.events, ruleForm.actions, t]);

  // ─── Selected item details ───
  const selectedRuleItem = selectedItem ? ruleForm[selectedItem.type]?.[selectedItem.index] : null;
  const selectedMeta = selectedItem
    ? allItems.find((i) => i.type === selectedItem.type && i.index === selectedItem.index)
    : null;

  // ─── Render ───
  if (!ruleId) {
    return (
      <div className="w-full overflow-hidden" style={{ height: currentSize.height - 40 }}>
        <RuleList />
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden" style={{ height: currentSize.height - 40 }}>
      <div className="flex flex-col h-full bg-gray-50 font-sans">
        {/* ── Tab bar ── */}
        <div className="flex items-center px-4 bg-white border-b border-gray-200">
          <div className="flex items-center flex-1">
            <button
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-medium border-b-2 cursor-pointer transition-colors -mb-px ${activeTab === "editor" ? "text-gray-900 border-gray-900" : "text-gray-500 border-transparent hover:text-gray-700"}`}
              onClick={() => setActiveTab("editor")}
            >
              <PenLine className="w-3.5 h-3.5" />
              {t("Editor")}
            </button>
            {!isNewRule && (
              <button
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-medium border-b-2 cursor-pointer transition-colors -mb-px ${activeTab === "runs" ? "text-gray-900 border-gray-900" : "text-gray-500 border-transparent hover:text-gray-700"}`}
                onClick={() => {
                  setActiveTab("runs");
                  setSelectedItem(null);
                }}
              >
                <History className="w-3.5 h-3.5" />
                {t("Run History")}
                <span className="text-[11px] font-medium bg-gray-100 text-gray-500 px-1.5 rounded-lg min-w-[18px] text-center">
                  {ruleMetrics
                    ? (ruleMetrics.toJS()?.completedCount || 0) +
                      (ruleMetrics.toJS()?.failedCount || 0) +
                      (ruleMetrics.toJS()?.inProgressCount || 0)
                    : 0}
                </span>
              </button>
            )}
          </div>

          {/* Active/Inactive toggle */}
          {!isNewRule && (
            <div className="flex items-center gap-2.5">
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  ruleForm.enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
                }`}
              >
                {ruleForm.enabled ? t("Active") : t("Inactive")}
              </span>
              <Switch
                checked={ruleForm.enabled}
                onChange={handleToggleEnabled}
                className={`${
                  ruleForm.enabled ? "bg-blue-600" : "bg-gray-200"
                } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`}
              >
                <span className="sr-only">{t("Toggle automation")}</span>
                <span
                  className={`${
                    ruleForm.enabled ? "translate-x-4" : "translate-x-0"
                  } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          )}
        </div>

        {/* ── Content ── */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT: Workflow builder inline or Runs */}
          <div className="flex-1 overflow-auto">
            {activeTab === "editor" ? (
              <div className="flex flex-col items-center py-10 px-6 min-h-full">
                {isLoadingRule ? (
                  <div className="p-8">
                    <ChartLoading height={200} label={t("Loading...")} />
                  </div>
                ) : (
                  <div className="w-full max-w-[420px] flex flex-col items-center">
                    {isManagedGtmRule ? (
                      <div className="mb-6 w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {t(
                          "Managed by GTM — this hourly sequence automation cannot be edited or deleted. You can still pause or resume it."
                        )}
                      </div>
                    ) : null}
                    {/* ── Trigger node ── */}
                    {ruleForm.events.map((ev: any, idx: number) => {
                      const data = ev.data || {};
                      const cat = getCategory(data, true);
                      const badge = getBadge(data, true);
                      const isValid = RuleValidator(ev)();
                      const isSelected =
                        selectedItem?.type === "events" && selectedItem?.index === idx;
                      const isEmpty = !ev.value;
                      return (
                        <div key={`ev-${idx}`} className="w-full flex flex-col items-center">
                          {isEmpty ? (
                            /* Empty trigger prompt */
                            <div
                              className={`w-full border-2 border-dashed rounded-xl px-6 py-8 flex flex-col items-center gap-3 cursor-pointer transition-all ${isSelected ? "border-blue-400 bg-blue-50/50" : "border-gray-300 hover:border-blue-300 hover:bg-blue-50/30"}`}
                              onClick={() => {
                                setSelectedItem({ type: "events", index: idx });
                                setSelectingBlock("trigger");
                                setBlockSearchQuery("");
                              }}
                            >
                              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#2563eb"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                </svg>
                              </div>
                              <div className="text-sm font-medium text-gray-700">
                                {t("Set a trigger in the sidebar")}
                              </div>
                              <div className="text-xs text-gray-400">
                                {t("Pick an event to start this workflow")}
                              </div>
                            </div>
                          ) : (
                            /* Configured trigger node */
                            <div
                              className={`w-full bg-white border rounded-xl shadow-sm cursor-pointer transition-all overflow-hidden ${isSelected ? "border-blue-500 ring-2 ring-blue-500/20" : "border-blue-300 hover:border-blue-400 hover:shadow-md"}`}
                              onClick={() => {
                                setSelectedItem({ type: "events", index: idx });
                                setSelectingBlock(null);
                              }}
                            >
                              {/* Content section */}
                              <div className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div
                                    className="w-6 h-6 min-w-6 rounded-md flex items-center justify-center"
                                    style={{ background: getCategoryColors(cat).bg }}
                                  >
                                    <RuleItemAppIcon data={data} itemType={RuleItemTypes.EVENT} />
                                  </div>
                                  <div className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
                                    {data.description || ev.label || data.title}
                                  </div>
                                  <span
                                    className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md ${getBadgeColor(badge)}`}
                                  >
                                    <svg
                                      width="10"
                                      height="10"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"></path>
                                    </svg>
                                    {badge}
                                  </span>
                                  <CheckCircleIcon
                                    className={`w-4 h-4 shrink-0 ${isValid ? "text-green-500" : "text-amber-400"}`}
                                  />
                                </div>
                              </div>
                              {data.description && (
                                <div className="px-4 pb-3 pt-0 border-t border-gray-100 mt-0">
                                  <div className="text-xs text-gray-500 pt-2">
                                    {data.description}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* ── Only show actions after trigger is selected ── */}
                    {ruleForm.events[0]?.value && (
                      <>
                        {/* ── Vertical connector ── */}
                        <div className="flex flex-col items-center py-1">
                          <div className="w-px h-6 bg-gradient-to-b from-gray-300 to-gray-400" />
                          <div className="w-3 h-3 rounded-full border-2 border-gray-400 bg-white shadow-sm" />
                          <div className="w-px h-6 bg-gradient-to-b from-gray-400 to-gray-300" />
                        </div>

                        {/* ── Action nodes ── */}
                        {ruleForm.actions.map((act: any, idx: number) => {
                          const data = act.data || {};
                          const cat = getCategory(data, false);
                          const badge = getBadge(data, false);
                          const isValid = RuleValidator(act)();
                          const isSelected =
                            selectedItem?.type === "actions" && selectedItem?.index === idx;
                          const isEmpty = !act.value;
                          return (
                            <div key={`act-${idx}`} className="w-full flex flex-col items-center">
                              {idx > 0 && (
                                /* Connector between actions */
                                <div className="flex flex-col items-center py-1">
                                  <div className="w-px h-4 bg-gradient-to-b from-gray-300 to-gray-400" />
                                  <div className="w-2.5 h-2.5 rounded-full border-2 border-gray-400 bg-white shadow-sm" />
                                  <div className="w-px h-4 bg-gradient-to-b from-gray-400 to-gray-300" />
                                </div>
                              )}
                              {isEmpty ? (
                                /* Empty action: dashed prompt */
                                <div
                                  className={`w-full border-2 border-dashed rounded-xl px-6 py-6 flex flex-col items-center gap-2.5 cursor-pointer transition-all ${isSelected ? "border-blue-400 bg-blue-50/50" : "border-gray-300 hover:border-blue-300 hover:bg-blue-50/30"}`}
                                  onClick={() => {
                                    setSelectedItem({ type: "actions", index: idx });
                                    setSelectingBlock("action");
                                    setBlockSearchQuery("");
                                  }}
                                >
                                  <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                                    <svg
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#16a34a"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                  </div>
                                  <div className="text-sm font-medium text-gray-700">
                                    {t("Select an action")}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {t("Pick what happens next")}
                                  </div>
                                </div>
                              ) : (
                                /* Configured action node - AI/Agent cards get purple border */
                                <div
                                  className={`w-full bg-white border rounded-xl shadow-sm cursor-pointer transition-all overflow-hidden ${
                                    cat === "ai"
                                      ? isSelected
                                        ? "border-violet-500 ring-2 ring-violet-500/20"
                                        : "border-violet-300 hover:border-violet-400 hover:shadow-md"
                                      : isSelected
                                        ? "border-blue-500 ring-2 ring-blue-500/20"
                                        : "border-blue-300 hover:border-blue-400 hover:shadow-md"
                                  }`}
                                  onClick={() => {
                                    setSelectedItem({ type: "actions", index: idx });
                                    setSelectingBlock(null);
                                  }}
                                >
                                  {/* Content section */}
                                  <div className="px-4 py-3">
                                    <div className="flex items-center gap-2.5">
                                      <div
                                        className="w-6 h-6 min-w-6 rounded-md flex items-center justify-center"
                                        style={{ background: getCategoryColors(cat).bg }}
                                      >
                                        <RuleItemAppIcon
                                          data={data}
                                          itemType={RuleItemTypes.ACTION}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
                                        {data.description || act.label || data.title}
                                      </div>
                                      <span
                                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md shrink-0 ${getBadgeColor(badge)}`}
                                      >
                                        <svg
                                          width="10"
                                          height="10"
                                          viewBox="0 0 16 16"
                                          fill="currentColor"
                                        >
                                          <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"></path>
                                        </svg>
                                        {badge}
                                      </span>
                                      <CheckCircleIcon
                                        className={`w-4 h-4 shrink-0 ${isValid ? "text-green-500" : "text-amber-400"}`}
                                      />
                                      {!isManagedGtmRule && idx > 0 && (
                                        <button
                                          className="w-5 h-5 flex items-center justify-center rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 border-none bg-transparent cursor-pointer transition-colors p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveRuleItem("actions", idx);
                                          }}
                                          title={t("Delete")}
                                        >
                                          <TrashIcon className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      {!isManagedGtmRule && isValid && (
                                        <button
                                          className="w-5 h-5 flex items-center justify-center rounded-md text-gray-300 hover:text-blue-500 hover:bg-blue-50 border-none bg-transparent cursor-pointer transition-colors p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddRuleItem("actions", idx);
                                          }}
                                          title={t("Add action")}
                                        >
                                          <PlusIcon className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {data.description &&
                                    act.label &&
                                    data.description !== act.label && (
                                      <div className="px-4 pb-3 pt-0 border-t border-gray-100 mt-0">
                                        <div className="text-xs text-gray-500 pt-2">
                                          {data.description}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* ── Add action button ── */}
                        {!isManagedGtmRule && ruleForm.events[0]?.value && (
                          <div className="flex flex-col items-center pt-1">
                            <div className="w-px h-4 bg-gray-300" />
                            <button
                              className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-white hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all group"
                              title={t("Add action")}
                              onClick={() =>
                                handleAddRuleItem("actions", ruleForm.actions.length - 1)
                              }
                            >
                              <PlusIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* ── Save / Cancel ── */}
                    <div className="flex gap-3 pt-8 items-center justify-center w-full">
                      {!isManagedGtmRule ? (
                        <button
                          className={`px-5 py-2 rounded-lg text-sm font-medium border-none bg-blue-600 text-white shadow-sm transition-all ${!isValidRule() ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98]"}`}
                          onClick={handleSave}
                          disabled={!isValidRule()}
                        >
                          {rule.get("id") ? t("Update Automation") : t("Create Automation")}
                        </button>
                      ) : null}
                      <Link
                        className="px-5 py-2 rounded-lg text-sm font-medium cursor-pointer bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700 transition-all no-underline"
                        to={`/workspaces/${projectId}/automations`}
                      >
                        {isManagedGtmRule ? t("Back") : t("Cancel")}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <RuleLogList showLog={{ ruleId }} projectId={projectId} sidebar />
            )}
          </div>

          {/* RIGHT: Selected item editor, Select block panel, or Checklist */}
          {activeTab === "editor" &&
            (selectingBlock ? (
              /* ── Select trigger/action panel ── */
              <div className="w-[420px] bg-white border-l border-gray-200 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="shrink-0 px-4 pt-4 pb-3">
                  <button
                    className="w-6 h-6 border-none bg-transparent cursor-pointer text-gray-400 p-0 flex items-center justify-center mb-3 hover:text-gray-900"
                    onClick={() => {
                      setSelectingBlock(null);
                      setBlockSearchQuery("");
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M7.78 12.53a.75.75 0 01-1.06 0L2.47 8.28a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 1.06L4.56 7.25h8.69a.75.75 0 010 1.5H4.56l3.22 3.22a.75.75 0 010 1.06z" />
                    </svg>
                  </button>
                  <div className="text-base font-semibold text-gray-900">
                    {selectingBlock === "trigger" ? t("Select trigger") : t("Select action")}
                  </div>
                  <div className="text-[13px] text-gray-500 mt-0.5">
                    {selectingBlock === "trigger"
                      ? t("Pick an event to start this workflow")
                      : t("Pick an action to add to this workflow")}
                  </div>
                </div>

                {/* Search */}
                <div className="shrink-0 px-4 pb-3">
                  <div className="relative">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      placeholder={
                        selectingBlock === "trigger"
                          ? t("Search triggers...")
                          : t("Search actions...")
                      }
                      value={blockSearchQuery}
                      onChange={(e) => setBlockSearchQuery(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Categorized list */}
                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  {(selectingBlock === "trigger" ? triggerCategories : actionCategories).map(
                    (cat) => {
                      const sourceItems =
                        selectingBlock === "trigger" ? eventDefinitions : actionDefinitions;
                      const filteredItems = sourceItems.filter((item: any) => {
                        if (!cat.filter(item)) return false;
                        if (blockSearchQuery) {
                          const q = blockSearchQuery.toLowerCase();
                          return (
                            item.label?.toLowerCase().includes(q) ||
                            item.data?.description?.toLowerCase().includes(q)
                          );
                        }
                        return true;
                      });
                      if (filteredItems.length === 0) return null;

                      const currentValue =
                        selectingBlock === "trigger"
                          ? ruleForm.events[selectedItem?.index ?? 0]?.value
                          : ruleForm.actions[selectedItem?.index ?? 0]?.value;

                      return (
                        <div key={cat.key} className="mb-4">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                            <cat.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {cat.label}
                          </div>
                          <div className="space-y-1">
                            {filteredItems.map((item: any) => (
                              <button
                                key={item.value}
                                type="button"
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left border-none cursor-pointer ${
                                  currentValue === item.value
                                    ? "bg-blue-50 text-blue-700"
                                    : "bg-white text-gray-900 hover:bg-gray-50"
                                }`}
                                onClick={() => {
                                  if (selectedItem) {
                                    let selectedItemData = item;
                                    // Inject inputType for SET_FIELD and AI_FIELD actions (same as RuleItemBuilder dropdown)
                                    if (
                                      selectingBlock === "action" &&
                                      (item.data?.operator === "SET_FIELD" ||
                                        item.data?.type === "AI_FIELD")
                                    ) {
                                      const actionInputType = getActionInputType(
                                        selectedItem.index
                                      );
                                      selectedItemData = {
                                        ...item,
                                        data: {
                                          ...item.data,
                                          inputType: actionInputType,
                                          outputType: actionInputType,
                                        },
                                      };
                                    }
                                    handleFieldChange(
                                      selectedItem.type,
                                      selectedItem.index,
                                      selectedItemData
                                    );
                                    setSelectingBlock(null);
                                    setBlockSearchQuery("");
                                  }
                                }}
                              >
                                <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                                  <RuleItemAppIcon
                                    data={item.data}
                                    itemType={
                                      selectingBlock === "trigger"
                                        ? RuleItemTypes.EVENT
                                        : RuleItemTypes.ACTION
                                    }
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">
                                    {item.data?.description || item.label}
                                  </div>
                                </div>
                                {currentValue === item.value && (
                                  <svg
                                    className="w-5 h-5 text-blue-600 shrink-0"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            ) : selectedItem && selectedRuleItem ? (
              <div className="w-[420px] bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
                {/* Header */}
                <div className="flex items-start p-4 gap-2.5 border-b border-gray-100">
                  <button
                    className="w-6 h-6 border-none bg-transparent cursor-pointer text-gray-500 p-0 flex items-center justify-center shrink-0 mt-0.5 hover:text-gray-900"
                    onClick={() => setSelectedItem(null)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M7.78 12.53a.75.75 0 01-1.06 0L2.47 8.28a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 1.06L4.56 7.25h8.69a.75.75 0 010 1.5H4.56l3.22 3.22a.75.75 0 010 1.06z" />
                    </svg>
                  </button>
                  {selectedMeta && (
                    <>
                      <div
                        className="w-8 h-8 min-w-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          background: getCategoryColors(selectedMeta.category).bg,
                          color: getCategoryColors(selectedMeta.category).fill,
                        }}
                      >
                        <RuleItemAppIcon
                          data={selectedRuleItem?.data || {}}
                          itemType={
                            selectedItem.type === "events"
                              ? RuleItemTypes.EVENT
                              : RuleItemTypes.ACTION
                          }
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-gray-400 font-medium">
                          {selectedMeta.badge}
                        </div>
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {selectedMeta.label.replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex gap-0.5 shrink-0">
                    {/* Change block button */}
                    {!isManagedGtmRule ? (
                      <button
                        className="w-7 h-7 border-none bg-transparent rounded-md cursor-pointer text-gray-400 flex items-center justify-center p-0 hover:bg-gray-100 hover:text-gray-600"
                        title={
                          selectedItem.type === "events" ? t("Change trigger") : t("Change block")
                        }
                        onClick={() => {
                          setSelectingBlock(selectedItem.type === "events" ? "trigger" : "action");
                          setBlockSearchQuery("");
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M4 6l4-4 4 4" />
                          <path d="M4 10l4 4 4-4" />
                        </svg>
                      </button>
                    ) : null}

                    {/* Delete block button */}
                    {!isManagedGtmRule &&
                      selectedItem.type === "actions" &&
                      selectedItem.index > 0 && (
                        <button
                          className="w-7 h-7 border-none bg-transparent rounded-md cursor-pointer text-gray-400 flex items-center justify-center p-0 hover:bg-red-50 hover:text-red-500"
                          title={t("Delete")}
                          onClick={() =>
                            handleRemoveRuleItem(selectedItem.type, selectedItem.index)
                          }
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                  </div>
                </div>

                {/* Description - Auto-resize textarea */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <textarea
                    ref={(el) => {
                      if (el) {
                        el.style.height = "auto";
                        el.style.height = el.scrollHeight + "px";
                      }
                    }}
                    className="w-full border-none outline-none text-[13px] text-gray-400 bg-transparent placeholder:text-gray-300 resize-none overflow-hidden"
                    placeholder={t("Add a description...")}
                    value={selectedRuleItem?.data?.description || ""}
                    readOnly={isManagedGtmRule}
                    onChange={(e) => {
                      if (isManagedGtmRule || !selectedItem || !selectedRuleItem) return;
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                      const updatedItem = {
                        ...selectedRuleItem,
                        data: {
                          ...(selectedRuleItem.data || {}),
                          description: e.target.value,
                        },
                      };
                      handleFieldChange(selectedItem.type, selectedItem.index, updatedItem);
                    }}
                    rows={1}
                    style={{ minHeight: "24px", height: "24px" }}
                  />
                </div>

                {/* Editor body */}
                <style>{`
                  .rule-item-builder-panel > div > .flex.w-full { flex-direction: column; gap: 12px; }
                  .rule-item-builder-panel > div > .flex.w-full > .w-fit { width: 100%; padding-right: 0; }
                  .rule-item-builder-panel > div > .flex.w-full > .w-fit .w-60 { width: 100%; }
                  .rule-item-builder-panel > div > .flex.w-full > div:last-child { width: 100%; }
                `}</style>
                <div className="p-4 flex-1 overflow-y-auto rule-item-builder-panel">
                  {/* Inputs section label */}
                  <div className="mb-3">
                    <div className="text-[13px] font-semibold text-gray-900 mb-1.5">
                      {t("Inputs")}
                    </div>
                  </div>

                  <RuleItemBuilder
                    rule={rule}
                    value={selectedRuleItem}
                    readonly={isManagedGtmRule}
                    lockedSelection={isManagedGtmRule || !!selectedRuleItem?.value}
                    isNewRule={isNewRule}
                    itemType={
                      selectedItem.type === "events" ? RuleItemTypes.EVENT : RuleItemTypes.ACTION
                    }
                    inputType={
                      selectedItem.type === "actions" ? getActionInputType(selectedItem.index) : ""
                    }
                    placeholder={
                      selectedItem.type === "events" ? t("When this happens") : t("Then do this")
                    }
                    onChange={(value: any) =>
                      handleFieldChange(selectedItem.type, selectedItem.index, value)
                    }
                  />

                  {/* ── Next step: always show, with add button for last action ── */}
                  {(() => {
                    const currentIdx = allItems.findIndex(
                      (i) => i.type === selectedItem.type && i.index === selectedItem.index
                    );
                    if (currentIdx < 0) return null;

                    const isLastItem = currentIdx >= allItems.length - 1;
                    const currentItem = allItems[currentIdx];
                    const nextItem = isLastItem ? null : allItems[currentIdx + 1];
                    const currentColors = getCategoryColors(currentItem.category);
                    const currentSource = (ruleForm as any)[currentItem.type]?.[currentItem.index];
                    const currentData = currentSource?.data || {};
                    const currentItemType =
                      currentItem.type === "events" ? RuleItemTypes.EVENT : RuleItemTypes.ACTION;

                    // If last action and valid, show "Add next step" button
                    if (isLastItem && currentItem.type === "actions" && currentItem.configured) {
                      return (
                        <div className="mt-6">
                          <div className="text-[13px] font-semibold text-gray-900 mb-1.5">
                            {t("Next step")}
                          </div>
                          <div className="text-xs text-gray-400 mb-2">
                            {t("Add the next block in this workflow")}
                          </div>
                          <div className="flex flex-col mt-2">
                            {/* Current item */}
                            <div className="flex items-center gap-2 px-2.5 py-2 border border-blue-500 rounded-lg bg-blue-50 text-[13px] text-gray-700">
                              <div
                                className="w-[22px] h-[22px] min-w-[22px] rounded-[5px] flex items-center justify-center"
                                style={{ background: currentColors.bg }}
                              >
                                <RuleItemAppIcon data={currentData} itemType={currentItemType} />
                              </div>
                              <span className="flex-1 text-[13px] text-gray-900 font-medium">
                                {currentItem.label}
                              </span>
                            </div>
                            {/* Add next step button */}
                            <div className="ml-5 pl-4 border-l border-gray-200">
                              <div className="text-[11px] text-gray-400 py-1.5">
                                {t("Next step")}
                              </div>
                              <button
                                className="flex items-center gap-2 w-full px-2.5 py-2 border border-dashed border-gray-300 rounded-lg bg-white text-[13px] text-gray-500 hover:border-blue-400 hover:text-blue-500 cursor-pointer transition-colors"
                                onClick={() =>
                                  handleAddRuleItem("actions", (ruleForm.actions.length || 1) - 1)
                                }
                              >
                                <PlusIcon className="w-4 h-4" />
                                <span>{t("Select block")}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (isLastItem) return null;

                    // Has next item - show both
                    const nextColors = getCategoryColors(nextItem!.category);
                    const nextSource = (ruleForm as any)[nextItem!.type]?.[nextItem!.index];
                    const nextData = nextSource?.data || {};
                    const nextItemType =
                      nextItem!.type === "events" ? RuleItemTypes.EVENT : RuleItemTypes.ACTION;

                    return (
                      <div className="mt-6">
                        <div className="text-[13px] font-semibold text-gray-900 mb-1.5">
                          {t("Next step")}
                        </div>
                        <div className="text-xs text-gray-400 mb-2">
                          {t("Add the next block in this workflow")}
                        </div>
                        <div className="flex flex-col mt-2">
                          {/* Current item (highlighted) */}
                          <div className="flex items-center gap-2 px-2.5 py-2 border border-blue-500 rounded-lg bg-blue-50 text-[13px] text-gray-700">
                            <div
                              className="w-[22px] h-[22px] min-w-[22px] rounded-[5px] flex items-center justify-center"
                              style={{ background: currentColors.bg }}
                            >
                              <RuleItemAppIcon data={currentData} itemType={currentItemType} />
                            </div>
                            <span className="flex-1 text-[13px] text-gray-900 font-medium">
                              {currentItem.label}
                            </span>
                          </div>

                          {/* Nested: sub-label + next item (indented with left border) */}
                          <div className="ml-5 pl-4 border-l border-gray-200">
                            <div className="text-[11px] text-gray-400 py-1.5">{t("Next step")}</div>

                            <div
                              className="flex items-center gap-2 px-2.5 py-2 border border-gray-200 rounded-lg bg-white text-[13px] text-gray-700 cursor-pointer hover:bg-gray-50"
                              onClick={() => {
                                setSelectedItem({ type: nextItem!.type, index: nextItem!.index });
                                // If unconfigured, open the Select Block panel
                                if (!nextItem!.configured) {
                                  setSelectingBlock(
                                    nextItem!.type === "events" ? "trigger" : "action"
                                  );
                                  setBlockSearchQuery("");
                                }
                              }}
                            >
                              <div
                                className="w-[22px] h-[22px] min-w-[22px] rounded-[5px] flex items-center justify-center"
                                style={{ background: nextColors.bg }}
                              >
                                <RuleItemAppIcon data={nextData} itemType={nextItemType} />
                              </div>
                              <span className="flex-1 text-[13px] text-gray-900 font-medium">
                                {nextItem!.label}
                              </span>
                              {nextItem!.type === "actions" && nextItem!.index > 0 && (
                                <button
                                  className="ml-auto w-[18px] h-[18px] border-none bg-transparent cursor-pointer text-gray-300 flex items-center justify-center p-0 rounded text-sm hover:text-red-500 hover:bg-red-50"
                                  title={t("Disconnect")}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveRuleItem(nextItem!.type, nextItem!.index);
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              /* Checklist panel (no item selected) */
              <div className="w-[420px] bg-white border-l border-gray-200 p-5 px-4 overflow-y-auto">
                <div className="flex items-center gap-2 mb-2">
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="#6366f1">
                    <path d="M8 0a.75.75 0 01.72.53l1.09 3.66 3.66 1.09a.75.75 0 010 1.44l-3.66 1.09-1.09 3.66a.75.75 0 01-1.44 0L6.19 7.81 2.53 6.72a.75.75 0 010-1.44l3.66-1.09L7.28.53A.75.75 0 018 0z" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-900">{workflowName}</span>
                </div>
                <div className="text-[13px] text-gray-500 leading-relaxed mb-5">
                  {t("This workflow triggers when a new record is created.")}
                </div>

                <div className="mb-3">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">
                    {t("Checklist")}
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    {t("Make sure all issues are resolved before publishing")}
                  </div>
                </div>

                {allItems.map((item, idx) => {
                  const colors = getCategoryColors(item.category);
                  const source = (ruleForm as any)[item.type]?.[item.index];
                  const data = source?.data || {};
                  const itemType =
                    item.type === "events" ? RuleItemTypes.EVENT : RuleItemTypes.ACTION;
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2.5 border-b border-gray-100 cursor-pointer gap-2 hover:bg-gray-50 hover:-mx-4 hover:px-4"
                      onClick={() => setSelectedItem({ type: item.type, index: item.index })}
                    >
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div
                          className="w-6 h-6 min-w-6 rounded-[5px] flex items-center justify-center"
                          style={{ background: colors.bg }}
                        >
                          <RuleItemAppIcon data={data} itemType={itemType} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[13px] font-medium text-gray-900 block truncate">
                            {item.label}
                          </span>
                          {!item.configured && (
                            <span className="flex items-center gap-1 text-xs text-amber-600 mt-0.5">
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="#f59e0b">
                                <path d="M8.22 1.754a.25.25 0 00-.44 0L1.698 13.132a.25.25 0 00.22.368h12.164a.25.25 0 00.22-.368L8.22 1.754z" />
                              </svg>
                              {t("This step still needs to be configured")}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-px rounded ${getBadgeColor(item.badge)}`}
                      >
                        <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"></path>
                        </svg>
                        {item.badge}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
